import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import axios from "axios";
import { API } from "../../../config";
import { ScenarioData } from "../actions/actionTypes";
import { getLocalizedTextByLocaleVersionTwo } from "../../../utils/localized-text";
import {
  addHours,
  addHoursSameTime,
  setZeroHrs,
} from "../dashboard/dashboardAsyncActions";

export const fetchCropsServiceDataTableFilter = createAsyncThunk(
  "DataFiltersReducer/fetchCropsServiceDataTableFilter",
  async (_, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;

    const token = user?.token ?? "";

    const headers = {
      Authorization: `Bearer ${token}`,
      "Accept-Language": `${locale}`,
    };

    try {
      const response = await axios.get(`${API}/core/cereal-type`, {
        headers,
      });
      const { data } = response;

      const dictionnary = data?.data?.reduce((obj: any, current: any) => {
        obj[current.slug] ??= {
          id: current.id,
          name: current.textIdLocale
            ? current.textIdLocale
            : current.generalName,
          isSelected: false,
          textId: current.textId.id,
        };
        return obj;
      }, {});

      const filtered = Object.keys(dictionnary).map((key) => dictionnary[key]);

      return store.fulfillWithValue(filtered);
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBSites = createAsyncThunk(
  "DataFiltersReducer/fetchSBSites",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/organization-geo-point?$select[]=name&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const locations = data.data.map((loc) => ({
        id: loc.id,
        name: loc.name,
        totalCapacity: loc.totalCapacity,
        shared: loc.shared ?? false,
      }));

      const filterLoc = locations.filter((v) => v.shared !== true);

      return filterLoc;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBUserNames = createAsyncThunk(
  "DataFiltersReducer/fetchSBUserNames",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/users/user?$select[]=username&$select[]=id&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;
      const usernames = data?.data?.map(
        (usernames: { username: string; id: number }) => ({
          name: usernames.username,
          id: usernames.id,
        }),
      );

      return usernames;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);

export const fetchSBActionsNames = createAsyncThunk(
  "DataFiltersReducer/fetchSBActionsNames",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const token = user?.token ?? "";
    try {
      const response = await axios.get(
        `${API}/core/scenario?isSystemScenario=false&deletedAt=null&statusConstantId[$in][]=34&statusConstantId[$in][]=35&$eager=[scopes.[group], cerealType.text.[localizedTexts.[locale]], statusConstant, name.[localizedTexts.[locale]], desc.[localizedTexts.[locale]]]&$limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": `${locale}`,
          },
        },
      );

      const { data } = response;

      const actionsValues = data.data.map((row: ScenarioData) => {
        const scenarioNameValue = () => {
          if (row.name) {
            let def = "";
            const arr = row.name.localizedTexts
              .map((c: any) => {
                if (locale === c.locale.value) {
                  return c.translation;
                }
                def = c.translation;
              })
              .filter((val: any) => null != val);
            if (!arr.length) {
              return [def].toString();
            } else {
              return arr.toString();
            }
          } else {
            return "Null";
          }
        };

        const tableRows = {
          name: scenarioNameValue().toLowerCase(),
          id: row.id,
          userFacingNameTextId: row.userFacingNameTextId,
          cerealType: getLocalizedTextByLocaleVersionTwo(
            locale,
            row.cerealType.text.localizedTexts,
          ),
        };
        return tableRows;
      });

      return actionsValues;
    } catch (error) {
      return error;
    }
  },
);

const buildDateParams = (dataDateActivity: Date[]) => {
  const fromDate = `createdAt[$gt]=${addHours(
    setZeroHrs(dataDateActivity[0]),
  ).toISOString()}`;

  const toDate = `createdAt[$lt]=${addHoursSameTime(
    setZeroHrs(dataDateActivity[1]),
  ).toISOString()}`;

  return { fromDate, toDate };
};

export const buildDateActivityParams = (dataDateActivity: Date[]) => {
  const { fromDate, toDate } = buildDateParams(dataDateActivity);

  return [fromDate, toDate].filter(Boolean).join("&");
};

export const exportData = createAsyncThunk(
  "dataExport/exportData",
  async (_, store) => {
    const { user } = (store.getState() as RootState).Authentication;
    const { email, filtersToExport } = (store.getState() as RootState)
      .DataExportReducer;
    const { columns } = (store.getState() as RootState).DataTableReducer;

    const token = user?.token ?? "";
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const columnsFiltered = columns
      ?.filter((col: any) => col.field !== "id")
      .map((col: any) => ({
        name: col.field,
        columnType: col.columnType,
        localizedName: col.headerName,
        hidden: false,
      }));

    const fromDate = filtersToExport?.datesRange?.fromDate;
    const toDate = filtersToExport?.datesRange?.toDate;

    const processedFromDate = addHours(setZeroHrs(fromDate));
    const processedToDate = addHoursSameTime(setZeroHrs(toDate));

    const resolveFilter = (arr: any[] | null | undefined) =>
      arr && arr.length > 0 ? arr : undefined;

    const filters = {
      datesRange: {
        fromDate: processedFromDate.toISOString(),
        toDate: processedToDate.toISOString(),
      },
      cropsTextIds: resolveFilter(filtersToExport?.cropsTextIds),
      scenarioNamesTextIds: resolveFilter(
        filtersToExport?.scenarioNamesTextIds,
      ),
      usernames: resolveFilter(filtersToExport?.usernames),
      geoPointNames: resolveFilter(filtersToExport?.geoPointNames),
    };

    const body = {
      email,
      columns: columnsFiltered,
      filters,
    };

    try {
      const response = await axios.post(`${API}/portal/data-export`, body, {
        headers,
      });

      return response;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  },
);
